import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FilterDropdownButton } from 'src/components/Common/Button'
import { AutoCompleteOption } from 'src/components/Common/FjFilter'
import FjPopover, { FjPopoverContent } from 'src/components/Common/FjPopover'
import { SfOpportunity } from 'src/models/SfOpportunity'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import { DEFAULT_SELECT_PAGE_SIZE } from 'src/constants/app'

type OpportunityFilterProps = {
  clearable?: boolean
  value?: string
  onChange: (opportunityId: string, opportunityName: string) => void
}

export const OpportunityFilter: React.FC<OpportunityFilterProps> = ({ clearable = true, value, onChange }) => {
  const [opportunityOptions, setOpportunityOptions] = useState<AutoCompleteOption[]>([])
  const [initialOpportunityOptions, setInitialOpportunityOptions] = useState<AutoCompleteOption[]>([])
  const [selectedOpportunityOption, setSelectedOpportunityOption] = useState<AutoCompleteOption>()

  const getSelectedOpportunity = useCallback(async () => {
    const selectedOpportunityFromOptions = [...initialOpportunityOptions, ...opportunityOptions].find(
      (option) => option.id === value
    )
    if (selectedOpportunityFromOptions) {
      setSelectedOpportunityOption(selectedOpportunityFromOptions)
      return
    }
    try {
      const opportunity = await SfOpportunity.get(value)
      setSelectedOpportunityOption(convertOpportunityToAutoCompleteOption(opportunity))
    } catch (err) {
      sharedAppStateStore.handleError(err)
    }
  }, [value, initialOpportunityOptions, opportunityOptions])

  const fetchOpportunityOptions = async (search: string) => {
    try {
      const data = await SfOpportunity.list({
        search,
        fields: 'id,name',
        page_size: DEFAULT_SELECT_PAGE_SIZE,
      })
      const options = data.data.map(convertOpportunityToAutoCompleteOption)
      if (initialOpportunityOptions.length === 0 && !search) {
        setInitialOpportunityOptions(options)
      }
      setOpportunityOptions(options)
    } catch (err) {
      sharedAppStateStore.handleError(err)
    }
  }

  useEffect(() => {
    fetchOpportunityOptions('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (value) {
      getSelectedOpportunity()
    } else {
      setSelectedOpportunityOption(undefined)
    }
  }, [getSelectedOpportunity, value])

  const convertOpportunityToAutoCompleteOption = (opportunity: SfOpportunity): AutoCompleteOption => ({
    key: opportunity.id,
    id: opportunity.id,
    value: opportunity.name,
    label: opportunity.name,
  })

  const showFilter = useMemo(() => {
    return initialOpportunityOptions.length > 0
  }, [initialOpportunityOptions])

  if (!showFilter) return null

  return (
    <FjPopover
      content={
        <FjPopoverContent
          options={opportunityOptions}
          values={selectedOpportunityOption ? [selectedOpportunityOption] : []}
          onSearch={(value: string) => fetchOpportunityOptions(value)}
          addFilterOption={(option) => onChange(option.id, option.label)}
        />
      }
    >
      <FilterDropdownButton
        selected={!!selectedOpportunityOption}
        title={selectedOpportunityOption ? `Opportunity | ${selectedOpportunityOption.label}` : 'Opportunity'}
        onClear={clearable ? () => onChange(undefined, undefined) : undefined}
      />
    </FjPopover>
  )
}
